import { useEffect, useMemo, useRef, useState } from 'react'
import { Play, Pause, RefreshCw, Search, AlertCircle, X, Volume2 } from 'lucide-react'
import { api, messaggioErrore } from '../../lib/api'
import { Button } from '../ui/Button'
import { Badge } from '../ui/Badge'
import { useVociElevenLabs } from '../../hooks/useVociElevenLabs'
import type { VoceElevenLabs } from '../../lib/tipi'
import styles from './VoiceSelector.module.css'

interface Props {
  valore?: string | null              // voice_id correntemente selezionato
  onChange: (voiceId: string | undefined) => void
  /** Testo da usare per l'anteprima audio (default in italiano) */
  testoAnteprima?: string
}

type FiltroCategoria = 'tutti' | 'gratis' | 'libreria'
type FiltroGenere = 'tutti' | 'male' | 'female'

/**
 * Selettore visuale di una voce ElevenLabs con anteprima audio.
 *
 * - Carica voci lazy: l'utente clicca un pulsante per popolare la lista
 *   (chiamata API costa, meglio farla on-demand)
 * - Filtri per nome, genere, categoria (gratis / libreria)
 * - Anteprima audio: prima prova `anteprima_url` diretta (più veloce),
 *   fallback al backend `/superadmin/elevenlabs/anteprima` se mancante
 */
export function VoiceSelector({ valore, onChange, testoAnteprima }: Props) {
  const { voci, caricamento, errore, caricate, caricaVoci, ricaricaVoci } = useVociElevenLabs()

  const [filtroTesto, setFiltroTesto] = useState('')
  const [filtroGenere, setFiltroGenere] = useState<FiltroGenere>('tutti')
  const [filtroCategoria, setFiltroCategoria] = useState<FiltroCategoria>('tutti')

  const [riproducendo, setRiproducendo] = useState<string | null>(null)
  const [erroreRiproduzione, setErroreRiproduzione] = useState<string | null>(null)
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const blobUrlRef = useRef<string | null>(null)

  // Stop audio quando il componente si smonta
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        try { audioRef.current.pause() } catch {/* ignora */}
      }
      if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current)
    }
  }, [])

  const vociFiltrate = useMemo(() => {
    return voci.filter((v) => {
      if (filtroCategoria === 'gratis' && !v.disponibile_gratis) return false
      if (filtroCategoria === 'libreria' && v.disponibile_gratis) return false
      if (filtroGenere !== 'tutti') {
        const g = (v.labels.gender ?? '').toLowerCase()
        if (filtroGenere === 'male' && !g.includes('male')) return false
        if (filtroGenere === 'female' && !g.includes('female')) return false
      }
      if (filtroTesto.trim()) {
        const t = filtroTesto.trim().toLowerCase()
        const cerca = `${v.nome} ${v.descrizione ?? ''} ${v.labels.accent ?? ''} ${v.labels.use_case ?? ''}`
          .toLowerCase()
        if (!cerca.includes(t)) return false
      }
      return true
    })
  }, [voci, filtroTesto, filtroGenere, filtroCategoria])

  const fermaRiproduzione = () => {
    if (audioRef.current) {
      try { audioRef.current.pause() } catch {/* ignora */}
      audioRef.current = null
    }
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current)
      blobUrlRef.current = null
    }
    setRiproducendo(null)
  }

  const ascoltaTramiteUrl = async (voce: VoceElevenLabs): Promise<boolean> => {
    if (!voce.anteprima_url) return false
    try {
      const audio = new Audio(voce.anteprima_url)
      audioRef.current = audio
      audio.onended = () => setRiproducendo(null)
      audio.onerror = () => setRiproducendo(null)
      await audio.play()
      return true
    } catch {
      return false
    }
  }

  const ascoltaTramiteBackend = async (voce: VoceElevenLabs): Promise<void> => {
    const res = await api.post<{ dati: { audio_base64: string; mime_type: string } }>(
      '/superadmin/elevenlabs/anteprima',
      { voice_id: voce.voice_id, ...(testoAnteprima ? { testo: testoAnteprima } : {}) },
    )
    const dati = res.data.dati
    if (!dati) throw new Error('Risposta vuota')
    const bin = atob(dati.audio_base64)
    const arr = new Uint8Array(bin.length)
    for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i)
    const blob = new Blob([arr], { type: dati.mime_type ?? 'audio/mpeg' })
    const url = URL.createObjectURL(blob)
    blobUrlRef.current = url
    const audio = new Audio(url)
    audioRef.current = audio
    audio.onended = () => {
      setRiproducendo(null)
      if (blobUrlRef.current === url) {
        URL.revokeObjectURL(url)
        blobUrlRef.current = null
      }
    }
    await audio.play()
  }

  const ascoltaVoce = async (voce: VoceElevenLabs) => {
    if (riproducendo === voce.voice_id) {
      fermaRiproduzione()
      return
    }
    fermaRiproduzione()
    setErroreRiproduzione(null)
    setRiproducendo(voce.voice_id)
    try {
      const ok = await ascoltaTramiteUrl(voce)
      if (!ok) await ascoltaTramiteBackend(voce)
    } catch (err) {
      setErroreRiproduzione(messaggioErrore(err))
      setRiproducendo(null)
    }
  }

  if (!caricate) {
    return (
      <div className={styles.statoIniziale}>
        <Volume2 size={28} color="var(--text-muted)" />
        <div className={styles.statoTitolo}>Voci ElevenLabs</div>
        <div className={styles.statoTesto}>
          Carica la lista delle voci disponibili sull'account per selezionarne una.
        </div>
        <Button
          variante="secondary"
          iconaSinistra={<RefreshCw size={14} />}
          onClick={() => caricaVoci()}
          caricamento={caricamento}
        >
          Carica voci da ElevenLabs
        </Button>
        {errore && (
          <div className={styles.banner}>
            <AlertCircle size={14} />
            <span>{errore}</span>
          </div>
        )}
      </div>
    )
  }

  if (caricamento && voci.length === 0) {
    return (
      <div className={styles.skeleton}>
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className={styles.skeletonRiga} />
        ))}
        <div className={styles.skeletonInfo}>Caricamento voci in corso…</div>
      </div>
    )
  }

  const voceSelezionata = voci.find((v) => v.voice_id === valore) ?? null

  return (
    <div className={styles.wrap}>
      {/* Voce attualmente selezionata */}
      {voceSelezionata ? (
        <div className={styles.selezionata}>
          <div>
            <div className={styles.selezionataLabel}>Voce selezionata</div>
            <div className={styles.selezionataNome}>
              {voceSelezionata.nome}
              <span className={styles.selezionataId}>
                <code>{voceSelezionata.voice_id}</code>
              </span>
            </div>
          </div>
          <button className={styles.rimuovi} onClick={() => onChange(undefined)}>
            <X size={14} /> Usa default
          </button>
        </div>
      ) : valore ? (
        <div className={styles.warning}>
          <AlertCircle size={14} />
          <span>
            Voice ID <code>{valore}</code> configurato ma non più disponibile su ElevenLabs.
          </span>
          <Button taglia="sm" variante="ghost" onClick={() => onChange(undefined)}>Rimuovi</Button>
        </div>
      ) : null}

      {/* Filtri */}
      <div className={styles.filtri}>
        <div className={styles.search}>
          <Search size={14} color="var(--text-muted)" />
          <input
            type="text"
            placeholder="Filtra voci…"
            value={filtroTesto}
            onChange={(e) => setFiltroTesto(e.target.value)}
          />
        </div>
        <select
          value={filtroGenere}
          onChange={(e) => setFiltroGenere(e.target.value as FiltroGenere)}
          className={styles.select}
        >
          <option value="tutti">Tutti i generi</option>
          <option value="male">Maschile</option>
          <option value="female">Femminile</option>
        </select>
        <select
          value={filtroCategoria}
          onChange={(e) => setFiltroCategoria(e.target.value as FiltroCategoria)}
          className={styles.select}
        >
          <option value="tutti">Tutte</option>
          <option value="gratis">Gratuite</option>
          <option value="libreria">Libreria</option>
        </select>
        <button
          className={styles.refresh}
          onClick={() => ricaricaVoci()}
          title="Ricarica voci"
        >
          <RefreshCw size={14} />
        </button>
      </div>

      {erroreRiproduzione && (
        <div className={styles.banner}>
          <AlertCircle size={14} />
          <span>{erroreRiproduzione}</span>
        </div>
      )}

      {/* Lista voci */}
      <div className={styles.lista}>
        {vociFiltrate.length === 0 ? (
          <div className={styles.vuoto}>Nessuna voce corrisponde ai filtri.</div>
        ) : (
          vociFiltrate.map((v) => {
            const selez = v.voice_id === valore
            const inRiproduzione = riproducendo === v.voice_id
            return (
              <label
                key={v.voice_id}
                className={`${styles.riga} ${selez ? styles.rigaSelez : ''}`}
              >
                <input
                  type="radio"
                  name="voice-selector"
                  checked={selez}
                  onChange={() => onChange(v.voice_id)}
                  className={styles.radio}
                />
                <div className={styles.rigaDati}>
                  <div className={styles.rigaNome}>
                    {v.nome}
                    {v.disponibile_gratis && <Badge variante="success">Gratis</Badge>}
                    {!v.disponibile_gratis && <Badge variante="warning">Libreria</Badge>}
                    {(v.labels.language === 'italian' || v.labels.accent === 'italian') && (
                      <Badge variante="accent">IT</Badge>
                    )}
                  </div>
                  <div className={styles.rigaMeta}>
                    {[v.labels.gender, v.labels.accent, v.labels.use_case]
                      .filter(Boolean)
                      .map((s) => s && s[0].toUpperCase() + s.slice(1))
                      .join(' · ')}
                  </div>
                </div>
                <button
                  type="button"
                  className={`${styles.btnAscolta} ${inRiproduzione ? styles.btnAscoltaAttivo : ''}`}
                  onClick={(e) => { e.preventDefault(); void ascoltaVoce(v) }}
                  title={inRiproduzione ? 'Ferma' : 'Ascolta'}
                >
                  {inRiproduzione ? <Pause size={14} /> : <Play size={14} />}
                </button>
              </label>
            )
          })
        )}
      </div>
    </div>
  )
}
